import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { Picture } from "~/components/ui/picture";
import { PriceDisplay } from "~/components/ui/price-display";
import type { ProductListRow } from "~/data/products-list";
import { useCurrencyPreference } from "~/lib/use-currency-preference";
import { useAddToEmbeddedCart, useEmbeddedCartCatalog } from "~/lib/use-embedded-cart";
import type { ProductSelection } from "~/lib/use-embedded-cart-global";
import { cn } from "~/lib/utils";
import { ResponsiveText } from "./ResponsiveText";

// Figma's row CTAs are Button size="sm" but 44px tall from md up (40px on mobile), like the PDP upsell card's h-11.
const CTA_CLASS = "min-w-[78px] shrink-0 px-4 text-base leading-5 tracking-[0.16px] md:h-11";

// One purchasable item under a product card: thumbnail, title (+ connector eyebrow), live
// price and a CTA. The price only shows for rows with a cart product code.
// Mobile: 40px thumbnail, short copy, eyebrow on its own line. md+: 102px thumbnail (126px from
// lg), full copy, eyebrow inline with the title ("J1772 Hardwired").
export function ProductRow({ row }: { row: ProductListRow }) {
  const { t } = useTranslation("products");
  // Screen readers get the full name ("J1772 Hardwired", "Dual-View Dash Cam"), never the short label.
  const name = row.eyebrow ? `${t(row.eyebrow)} ${t(row.title)}` : t(row.title);

  return (
    <li className="flex w-full items-center md:py-4 lg:pr-6 lg:pl-4">
      <div className="bg-bg-brand-subtle size-10 shrink-0 overflow-hidden rounded-xs md:size-[102px] md:rounded-md lg:size-[126px]">
        <Picture
          src={row.image}
          alt=""
          width={126}
          height={126}
          className={cn("size-full", row.imageFit === "cover" ? "object-cover" : "object-contain")}
        />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-6 py-1 pl-2 md:pl-5 lg:gap-3 xl:gap-6">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <p className="text-text-body-tertiary md:text-figma-body-l-size text-figma-body-sm-size flex min-w-0 flex-1 flex-col leading-5 font-bold tracking-[0.14px] md:block md:leading-[1.1] md:tracking-[0.18px]">
            {row.eyebrow && (
              <span className="text-bouncie-400 text-figma-body-xs-size leading-[18px] tracking-normal md:text-[length:inherit] md:leading-[inherit] md:tracking-[inherit] md:text-[color:inherit]">
                {t(row.eyebrow)}{" "}
              </span>
            )}
            {/* ResponsiveText can render bare text, which gives the plugin nothing to anchor on here. */}
            <span data-sid={`products-list-row-title:${row.id}`}>
              <ResponsiveText short={t(row.shortTitle ?? row.title)} long={t(row.title)} />
            </span>
          </p>
          {row.cta.kind === "cart" && row.cta.productCode && <RowPrice productCode={row.cta.productCode} />}
        </div>
        {row.cta.kind === "cart" && row.cta.productCode ? (
          <AddToCartButton
            data-sid={`products-list-row-add:${row.id}`}
            productCode={row.cta.productCode}
            selection={row.cta.selection}
            name={name}
          />
        ) : row.cta.kind === "cart" ? (
          // TODO(BNC-5657): no confirmed cart product code yet — looks as designed, does nothing until it has one.
          <Button
            data-sid={`products-list-row-add-pending:${row.id}`}
            type="button"
            variant="primary"
            size="sm"
            className={CTA_CLASS}
            aria-label={t("list.add.aria", { name })}
          >
            <ResponsiveText short={t("list.add.short")} long={t("list.add.long")} />
          </Button>
        ) : (
          <Button variant="primary" size="sm" className={CTA_CLASS} asChild>
            <Link data-sid={`products-list-row-link:${row.id}`} to={row.cta.href} aria-label={t(`${row.cta.label}.aria`, { name })}>
              <ResponsiveText short={t(`${row.cta.label}.short`)} long={t(`${row.cta.label}.long`)} />
            </Link>
          </Button>
        )}
      </div>
    </li>
  );
}

// Renders nothing until the embedded cart catalog has a price — Figma's "$00.00" is a placeholder.
function RowPrice({ productCode }: { productCode: string }) {
  const [currency] = useCurrencyPreference();
  const { currencySymbol, unitPrice } = useEmbeddedCartCatalog(productCode, currency);
  if (typeof unitPrice !== "number") return null;

  return (
    <PriceDisplay
      value={unitPrice}
      currencySymbol={currencySymbol ?? "$"}
      // fontSize is an inline style, so the md size needs !important to win.
      className="text-text-heading shrink-0 md:text-[18px]!"
      fontSize={14}
      weightClassName="font-bold"
    />
  );
}

// Rest props (the call site's data-sid) are forwarded to the DOM button.
function AddToCartButton({
  productCode,
  selection: rowSelection,
  name,
  ...props
}: { productCode: string; selection?: ProductSelection; name: string } & ButtonProps) {
  const { t } = useTranslation("products");
  const [currency] = useCurrencyPreference();
  const { attributes } = useEmbeddedCartCatalog(productCode, currency);
  // The row's own variant (e.g. a charger's connector and power regulation); any attribute it
  // doesn't name takes its first value, the same default as the PDP hero.
  const selection = useMemo<ProductSelection>(
    () =>
      Object.fromEntries(
        (attributes ?? []).map((attribute) => [
          attribute.key,
          rowSelection?.[attribute.key] ?? attribute.values[0]?.key ?? "",
        ]),
      ),
    [attributes, rowSelection],
  );
  const addToCart = useAddToEmbeddedCart(productCode, selection);

  return (
    <Button
      {...props}
      variant="primary"
      size="sm"
      className={CTA_CLASS}
      onClick={addToCart}
      aria-label={t("list.add.aria", { name })}
    >
      <ResponsiveText short={t("list.add.short")} long={t("list.add.long")} />
    </Button>
  );
}
